// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to TermsApiError.
// Activated by setting VITE_USE_MOCK_API=false; see terms.adapter.ts.
// Endpoint shapes follow docs/api-contracts/term-management.md.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

import {
  TermsApiError,
  type CreateTermTypeInput,
  type CreateTermVersionInput,
  type TermsApi,
  type TermsApiErrorCode,
  type TermType,
  type TermVersion,
  type TermVersionSummary,
  type UpdateTermTypeInput,
  type UpdateTermVersionInput,
} from '../types/terms.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` means the request never got an HTTP reply — offline, DNS, or
  // timeout. Classify as NETWORK (per error standard §3) so callers can show the
  // operation-specific failure message instead of a generic "unknown error".
  if (!candidate.response) {
    throw new TermsApiError('NETWORK', '네트워크 연결을 확인한 후 다시 시도해주세요.');
  }
  const payload = candidate.response?.data?.error;
  throw new TermsApiError(
    (payload?.code as TermsApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

const typesPath = (): string => `${endpoints.terms}/types`;
const typePath = (id: number): string => `${typesPath()}/${id}`;
const versionsPath = (typeId: number): string => `${typePath(typeId)}/versions`;
const versionPath = (typeId: number, versionId: number): string =>
  `${versionsPath(typeId)}/${versionId}`;

export const termsHttpApi: TermsApi = {
  async listTypes(): Promise<TermType[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<TermType[]>>(typesPath());
      const data = res.data?.data;
      // List iterates this; a non-array body (wrong envelope, HTML, paged
      // wrapper) must surface as a load error rather than white-screen the list.
      if (!Array.isArray(data)) {
        throw new TermsApiError('UNKNOWN', '약관 유형을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async createType(input: CreateTermTypeInput): Promise<TermType> {
    try {
      const res = await httpClient.post<ApiEnvelope<TermType>>(typesPath(), input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateType(id: number, input: UpdateTermTypeInput): Promise<TermType> {
    try {
      const res = await httpClient.patch<ApiEnvelope<TermType>>(typePath(id), input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteType(id: number): Promise<void> {
    try {
      await httpClient.delete(typePath(id));
    } catch (e) {
      mapError(e);
    }
  },

  async listVersions(typeId: number): Promise<TermVersionSummary[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<TermVersionSummary[]>>(versionsPath(typeId));
      const data = res.data?.data;
      // Version list iterates this; a non-array body must surface as a load
      // error rather than white-screen the list.
      if (!Array.isArray(data)) {
        throw new TermsApiError('UNKNOWN', '약관 버전을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async getVersion(typeId: number, versionId: number): Promise<TermVersion> {
    try {
      const res = await httpClient.get<ApiEnvelope<TermVersion>>(versionPath(typeId, versionId));
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async createVersion(typeId: number, input: CreateTermVersionInput): Promise<TermVersion> {
    try {
      const res = await httpClient.post<ApiEnvelope<TermVersion>>(versionsPath(typeId), input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateVersion(
    typeId: number,
    versionId: number,
    input: UpdateTermVersionInput,
  ): Promise<TermVersion> {
    try {
      const res = await httpClient.patch<ApiEnvelope<TermVersion>>(
        versionPath(typeId, versionId),
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  // Contract §3.9: 204 on success; FE just toasts and invalidates queries.
  async publishVersion(typeId: number, versionId: number): Promise<void> {
    try {
      await httpClient.post(`${versionPath(typeId, versionId)}/publish`);
    } catch (e) {
      mapError(e);
    }
  },

  async deleteVersion(typeId: number, versionId: number): Promise<void> {
    try {
      await httpClient.delete(versionPath(typeId, versionId));
    } catch (e) {
      mapError(e);
    }
  },
};
